<template>
  <v-menu
    v-model="isMenuOpen"
    data-testid="mainMenu-menu-user"
    offset-y
    :close-on-content-click="false"
  >
    <template v-slot:activator="{ on }">
      <v-btn v-on="on" class="ml-4 mr-3" elevation="0" fab x-small>
        <UserAvatar
          size="32px"
          fontSize="10px"
          :account="account"
          :imageSize="IMAGE_SIZES.MINI"
        />
      </v-btn>
    </template>

    <v-list
      class="main-menu-list"
      data-testid="mainMenu-list-user"
      nav
      dense
      min-width="254"
    >
      <v-list-item>
        <v-list-item-content class="py-0">
          <v-list-item-title
            class="d-flex flex-column align-center justify-center"
          >
            <p class="subtitle-2 blue-grey--text text--darken-2 mb-1">
              {{ account.nickname ? account.nickname : userFullName }}
            </p>
            <p
              v-if="account.nickname"
              class="caption blue-grey--text text--darken-1 mb-1"
            >
              {{ userFullName }}
            </p>
            <p class="caption blue-grey--text text--darken-1 mb-1">
              {{ account.email }}
            </p>
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-divider></v-divider>

      <template v-if="shouldShowOrganizations">
        <v-list-group
          data-testid="mainMenu-item-organizations"
          no-action
          prepend-icon="mdi-domain"
        >
          <template v-slot:activator>
            <v-list-item-content class="py-0">
              <v-list-item-title
                class="blue-grey--text text--darken-2 d-flex align-center"
              >
                {{ $t('common.organization_other') }}
                <v-chip
                  v-if="hasPendingOrganizations"
                  class="ms-2 px-2"
                  color="primary"
                  data-testid="mainMenu-chip-pendingOrganizationsCount"
                  x-small
                >
                  {{ pendingOrganizations.length }}
                </v-chip>
              </v-list-item-title>
            </v-list-item-content>
          </template>

          <v-list-item
            v-for="organizationOption in accountOrganizations"
            :data-testid="`mainMenu-item-organization-${organizationOption.id}`"
            :disabled="isCurrentOrganization(organizationOption)"
            :key="organizationOption.id"
            @click="handleOrganizationClick(organizationOption)"
          >
            <v-list-item-content class="py-0">
              <v-list-item-title class="blue-grey--text text--darken-2 d-flex">
                {{ organizationOption.name }}
                <v-chip
                  v-if="isPendingInvitation(organizationOption)"
                  class="ms-2 px-2"
                  color="primary"
                  data-testid="mainMenu-chip-pendingInvitation"
                  x-small
                >
                  {{ $t('common.new') }}
                </v-chip>
              </v-list-item-title>
            </v-list-item-content>

            <v-list-item-icon
              v-if="isCurrentOrganization(organizationOption)"
              class="mr-1"
            >
              <v-icon color="primary" small>mdi-check</v-icon>
            </v-list-item-icon>
          </v-list-item>
        </v-list-group>

        <v-divider></v-divider>
      </template>

      <v-list-item
        class="mt-1"
        :data-tourid="`navigation-button-profile`"
        @click="goToProfile"
      >
        <v-list-item-icon class="mr-1">
          <v-icon color="blue-grey darken-2">mdi-account-circle-outline</v-icon>
        </v-list-item-icon>

        <v-list-item-content class="py-0">
          <v-list-item-title class="blue-grey--text text--darken-2">{{
            $t('common.profile')
          }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-list-item
        :data-tourid="`navigation-button-activity`"
        @click="goToActivityLogs"
      >
        <v-list-item-icon class="mr-1">
          <v-icon color="blue-grey darken-2">mdi-math-log</v-icon>
        </v-list-item-icon>

        <v-list-item-content class="py-0">
          <v-list-item-title class="blue-grey--text text--darken-2">{{
            $t('menu.user.my_activity_logs')
          }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-divider></v-divider>

      <v-list-item :data-tourid="`navigation-button-logout`" @click="logout">
        <v-list-item-icon class="mr-1">
          <v-icon color="blue-grey darken-2">mdi-logout</v-icon>
        </v-list-item-icon>

        <v-list-item-content class="py-0">
          <v-list-item-title class="blue-grey--text text--darken-2">{{
            $t('menu.user.log_out')
          }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </v-menu>
</template>
<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { IMAGE_SIZES } from 'rever-media-sdk'
import { utils } from 'frontend-js-shared'

import mixin from '@/mixins/mixin.vue'
import userMixin from '@/mixins/userMixin.vue'

import UserAvatar from '@/components/UserAvatar.vue'

export default {
  name: 'MainMenu',

  mixins: [mixin, userMixin],

  components: {
    UserAvatar,
  },

  data() {
    return {
      IMAGE_SIZES,
      isMenuOpen: false,
    }
  },

  mounted() {
    this.fetchAccountOrganizations().catch(() => {})
  },

  computed: {
    ...mapGetters(['account', 'organizationId', 'accountOrganizations']),

    userFullName() {
      return this.getFullName(this.account)
    },

    shouldShowOrganizations() {
      return this.accountOrganizations.length > 1
    },

    pendingOrganizations() {
      return this.accountOrganizations.filter(organizationOption =>
        _.isEqual(
          organizationOption.status,
          this.$constants.accountStatuses.PENDING
        )
      )
    },

    hasPendingOrganizations() {
      return !_.isEmpty(this.pendingOrganizations)
    },
  },

  methods: {
    ...mapActions(['switchOrganization', 'fetchAccountOrganizations']),

    isCurrentOrganization(organization) {
      return utils.isSameId(organization.id, this.organizationId)
    },

    isPendingInvitation(organization) {
      return organization.status === this.$constants.accountStatuses.PENDING
    },

    shouldJoinOrganization(organization) {
      const unjoinedStatuses = [
        this.$constants.accountStatuses.PENDING,
        this.$constants.accountStatuses.CREATED,
      ]

      return _.includes(unjoinedStatuses, organization?.status)
    },

    async handleOrganizationClick(organization) {
      if (this.isCurrentOrganization(organization)) return

      this.$track('menu.Navigate', this.$store, {
        option: `switchOrganization-${organization.id}`,
      })
      this.isMenuOpen = false

      try {
        await this.switchOrganization({
          organizationId: organization.id,
          shouldJoin: this.shouldJoinOrganization(organization),
          organizationName: organization.name,
        })
      } catch (error) {
        this.showError(
          this.$t('organization_settings.switch_organization.error')
        )
      }
    },

    goToProfile() {
      this.isMenuOpen = false
      this.$track('menu.Navigate', this.$store, { option: 'profile' })

      this.navigateTo({
        name: 'profile',
        params: {
          organizationId: this.organizationId,
          userId: this.account._id,
        },
      })
    },

    goToActivityLogs() {
      this.isMenuOpen = false
      this.$track('menu.Navigate', this.$store, { option: 'activity' })
      this.navigateTo({ name: 'accountAuditLogs' })
    },

    logout() {
      this.isMenuOpen = false
      this.$track('menu.Navigate', this.$store, { option: 'logout' })
      this.navigateTo({ name: 'logout' })
    },
  },
}
</script>
<style lang="scss">
.main-menu-list {
  > .v-list-group {
    .v-list-group__header__prepend-icon {
      justify-content: flex-start;
      margin-right: 4px;
      min-width: 24px;
    }
  }

  &.v-list--nav.v-list--dense {
    > .v-list-group > .v-list-group__items > .v-list-item {
      padding-left: 36px;
    }
  }
}
</style>
